/// <amd-module name="Dashboards/Grid/DashboardGrid" />
import * as React from "react";
import * as ReactGridLayout from "react-grid-layout";

import { GridItem, GridItemElement, GridItemPosition, GridItemProps } from "./DashboardGridItem";
import { assignPositionsToGridItems } from "./GridPlacement";
import { WidgetSizeConverter } from "./WidgetSizeConverter";

export const enum GridMode {
    Readonly,
    Editable
}

export interface DashboardGridProps {
    children: GridItemElement | GridItemElement[];
    mode: GridMode;
    /**
     * The last column to search for a vacancy before wrapping to the next row when
     * finding a placement for unpositioned grid items.
     * Supported values: 1-32
     */
    wrappingColumn: number;
    onGridItemsMoved: (gridItemPositions: GridItemPosition[]) => void;

    /**
     * Called when the grid is in Readonly mode and is given no grid items to render.
     * The returned result is rendered as the content of the grid.
     */
    renderOnEmpty: () => JSX.Element | null;
}

interface DashboardGridState {
    /** 0-indexed. For ReactGridLayout to support dynamic horizontal resizing. */
    gridColumnCount: number;

    /**
     * User Story #1466356
     * Indicates whether the React lifecycle update immediately preceding this update rendered the grid instead of
     * the empty experience.
     *
     * Used to switch what programmatic width styles to use for first render vs subsequent render of the grid
     * to work around some visual issues with ReactGridLayout. See consuming code for more descriptions of the
     * issues.
     */
    precedingUpdateRenderedGrid: boolean;
}

/** Renders a grid of items that supports drag-n-drop. */
export const DashboardGrid = React.forwardRef<HTMLDivElement, { children?: React.ReactNode } & DashboardGridProps>((props, ref) => (
    <DashboardGridImplementation forwardRef={ref} {...props} />
));

interface DashboardGridImplementationProps extends DashboardGridProps {
    /** Allows consumers of DashboardGrid to provide a ref that is forwarded to the outermost element of the grid. */
    forwardRef: React.Ref<HTMLDivElement> | undefined;
}

class DashboardGridImplementation extends React.Component<DashboardGridImplementationProps, DashboardGridState> {
    private static readonly gridMargin: [number, number] = [WidgetSizeConverter.CellMarginWidth, WidgetSizeConverter.CellMarginHeight];

    /** 0-indexed max column on the dashboard */
    private static readonly maxColumn: number = 24;

    private static getRightmostOccupiedColumnOfGridItems(gridItems: GridItemElement[]): number {
        return Math.max(
            0,
            ...gridItems.map(
                gridItem => gridItem.props.column + gridItem.props.columnSpan - 1 // Make 0-indexed
            )
        );
    }

    public static getDerivedStateFromProps(props: DashboardGridImplementationProps, state: DashboardGridState): DashboardGridState | null {
        const gridItems = React.Children.toArray(props.children) as GridItemElement[];
        let newGridColumnCount = 0;
        if (gridItems.length > 0) {
            newGridColumnCount = Math.max(state.gridColumnCount, DashboardGridImplementation.getRightmostOccupiedColumnOfGridItems(gridItems));
        }

        if (newGridColumnCount === state.gridColumnCount) {
            return null;
        } else {
            return {
                ...state,
                gridColumnCount: newGridColumnCount
            };
        }
    }

    public constructor(props: DashboardGridImplementationProps) {
        super(props);
        this.throwIfChildrenAreNotGridItems();

        this.state = {
            gridColumnCount: 0,
            precedingUpdateRenderedGrid: false
        };
    }

    public componentDidMount(): void {
        const renderedEmptyExperience = this.showEmptyExperience();
        if (!renderedEmptyExperience !== this.state.precedingUpdateRenderedGrid) {
            this.setState({ precedingUpdateRenderedGrid: !renderedEmptyExperience });
        }
    }

    public componentDidUpdate(): void {
        const currentUpdateRenderedEmptyExperience = this.showEmptyExperience();
        if (currentUpdateRenderedEmptyExperience) {
            // We rely on ReactGridLayout to notify us of grid layout updates.
            // But since we don't render ReactGridLayout for the empty experience, we have to
            // notify the layout change ourselves.
            if (this.state.precedingUpdateRenderedGrid) {
                this.onLayoutChange([]);
            }
        }

        if (currentUpdateRenderedEmptyExperience === this.state.precedingUpdateRenderedGrid) {
            this.setState({ precedingUpdateRenderedGrid: !currentUpdateRenderedEmptyExperience });
        }
    }

    public render(): JSX.Element | null {
        this.throwIfChildrenAreNotGridItems();

        const gridItems = React.Children.toArray(this.props.children) as GridItemElement[];
        const placedGridItems = assignPositionsToGridItems(gridItems, this.props.wrappingColumn);
        const gridColumnCount = this.computeGridColumnCount(gridItems, placedGridItems);

        const { gridItemsWithLayouts, gridItemLayouts } = this.applyLayoutsToGridItems(placedGridItems);
        const sortedGridItems = this.sortGridItems(gridItemsWithLayouts);

        return this.renderGrid(sortedGridItems, gridItemLayouts, gridColumnCount);
    }

    private computeGridColumnCount(gridItems: GridItemElement[], placedGridItems: GridItemElement[]): number {
        // We may have placed some unplaced widgets, so the number of columns may differ from what's in state
        let count =
            gridItems === placedGridItems
                ? this.state.gridColumnCount
                : Math.max(this.state.gridColumnCount, DashboardGridImplementation.getRightmostOccupiedColumnOfGridItems(placedGridItems));

        // Constrain to the maximum number of columns allowed on a dashboard
        count = Math.min(DashboardGridImplementation.maxColumn, count);

        return count;
    }

    private sortGridItems(unsortedItems: ReadonlyArray<GridItemElement>): GridItemElement[] {
        return unsortedItems.slice().sort(gridItemSortComparer);
    }

    /**
     * Clones grid items with "data-grid" attribute property containing their layout for ReactGridLayout.
     * Returns the layouts and the grid item clones with the additional attribute.
     */
    private applyLayoutsToGridItems(
        gridItems: ReadonlyArray<GridItemElement>
    ): { gridItemLayouts: ReactGridLayout.Layout[]; gridItemsWithLayouts: GridItemElement[] } {
        /* A bug of react-grid-layout is that passing the layout prop to the grid alone doesn't
         * position grid items. So we must add "data-grid" attributes to the grid items.
         * However another bug is that when using ReactGridLayout as a managed component,
         * updating "data-grid" attributes doesn't reposition grid items.
         * So we both provide a layout prop so the grid recognizes updates to where grid items
         * are positioned, and "data-grid" attributes so it actually positions the grid items. */
        const gridItemLayouts: ReactGridLayout.Layout[] = [];
        const gridItemsWithLayouts: GridItemElement[] = [];

        for (const gridItem of gridItems) {
            const layout = this.gridItemPropsToLayout(gridItem.props);
            gridItemLayouts.push(layout);
            gridItemsWithLayouts.push(React.cloneElement(gridItem, { "data-grid": layout }));
        }

        return { gridItemLayouts, gridItemsWithLayouts };
    }

    private computeGridWidth(gridColumnCount: number): number {
        // react-grid-layout width calculation is the combination of the width of
        // the cells across all columns and the width of a left and right margin on each cell.
        return WidgetSizeConverter.CellWidth * gridColumnCount + WidgetSizeConverter.CellMarginWidth * (gridColumnCount + 1);
    }

    /** Returns a styles object to provide to the grid props. */
    private getGridStyles(gridWidth: number): React.CSSProperties {
        /* User Story #1466356
         * ReactGridLayout has an issue where it incorrectly renders
         * the grid initially by using the width of the available space in the
         * parent container, squashing the grid items horizontally to fit rather
         * than using the grid width we provided. We rely on this initial render to
         * determine what grid items are in view. So we explicitly size the
         * width on first render of the container as a workaround.
         *
         * ReactGridLayout also doesn't seem to correctly calculate its element
         * width based on its grid item contents, so we provide a min-width
         * to make sure the grid tiling background pattern carries through the
         * entire grid even when scrolled horizontally. */
        return this.state.precedingUpdateRenderedGrid || this.showEmptyExperience() ? { minWidth: gridWidth } : { width: gridWidth };
    }

    private showEmptyExperience(): boolean {
        const editMode = this.props.mode === GridMode.Editable;
        return !editMode && React.Children.count(this.props.children) === 0;
    }

    private renderGrid(gridItems: GridItemElement[], gridItemLayouts: ReactGridLayout.Layout[], gridColumnCount: number): JSX.Element {
        const editMode = this.props.mode === GridMode.Editable;
        let className = "devops-dashboard-grid flex-grow";
        if (editMode) {
            className += " editable";
        }

        const gridWidth = this.computeGridWidth(gridColumnCount);

        return (
            <div className={className} ref={this.props.forwardRef} style={this.getGridStyles(gridWidth)}>
                {!this.showEmptyExperience() ? this.renderOSSGrid(gridItems, gridItemLayouts, gridColumnCount, gridWidth, editMode) : null}
                {this.showEmptyExperience() ? this.props.renderOnEmpty() : null}
            </div>
        );
    }

    private renderOSSGrid(
        gridItems: GridItemElement[],
        gridItemLayouts: ReactGridLayout.Layout[],
        gridColumnCount: number,
        gridWidth: number,
        editMode: boolean
    ): JSX.Element {
        return (
            <>
                {
                // @ts-ignore: isEditable is not in type definition yet. Note- The autoformatter likes to expand this comment. You must have ReactGridLayout IMMEDIATELY follow this line, or tsc will complain with TS2322.
                }<ReactGridLayout
                    cols={gridColumnCount}
                    width={gridWidth}
                    rowHeight={WidgetSizeConverter.CellHeight}
                    margin={DashboardGridImplementation.gridMargin}
                    isResizable={false}
                    isDraggable={true}
                    {...{ isEditable: editMode }}
                    layout={gridItemLayouts}
                    onLayoutChange={this.onLayoutChange}
                    onDrag={this.onDrag}
                >
                    {gridItems}
                </ReactGridLayout>
            </>
        );
    }

    private onLayoutChange = (layouts: ReactGridLayout.Layout[]) => {
        const gridItemPositions: GridItemPosition[] = layouts.map(this.layoutToGridItemPosition);
        this.props.onGridItemsMoved(gridItemPositions);

        const layoutsEndColumn = Math.max(0, ...layouts.map(l => l.x + l.w));
        this.setState({ gridColumnCount: layoutsEndColumn });
    };

    private onDrag = (
        layouts: ReactGridLayout.Layout[],
        draggedItemOldLayout: ReactGridLayout.Layout,
        draggedItemNewLayout: ReactGridLayout.Layout
    ) => {
        const { x, w } = draggedItemNewLayout;
        const draggedItemEndColumn = x + w;

        // Adds 1 to the dragged grid item end column so users can drag items infinitely to the right
        const newMaxColumn = Math.max(this.state.gridColumnCount, draggedItemEndColumn + 1);

        this.setState({ gridColumnCount: newMaxColumn });
    };

    private gridItemPropsToLayout(gridItemProps: GridItemProps): ReactGridLayout.Layout {
        return {
            i: gridItemProps.id,
            y: gridItemProps.row - 1, // Dashboard starts at (1,1) while react-grid-layout starts at (0,0)
            h: gridItemProps.rowSpan,
            x: gridItemProps.column - 1, // Dashboard starts at (1,1) while react-grid-layout starts at (0,0)
            w: gridItemProps.columnSpan,
            // @ts-ignore new field is not in type definition yet
            continueDrag: gridItemProps.dragEvent
        };
    }

    private layoutToGridItemPosition(layout: ReactGridLayout.Layout): GridItemPosition {
        return {
            id: layout.i!,
            row: layout.y + 1, // Dashboard starts at (1,1) while react-grid-layout starts at (0,0)
            rowSpan: layout.h,
            column: layout.x + 1, // Dashboard starts at (1,1) while react-grid-layout starts at (0,0)
            columnSpan: layout.w
        };
    }

    private throwIfChildrenAreNotGridItems(): void | never {
        if ($DEBUG) {
            React.Children.forEach<GridItemElement>(this.props.children, child => {
                if (!child.type || child.type !== GridItem) {
                    throw new Error("Only GridItems are allowed as children of the grid");
                }
            });
        }
    }
}

function gridItemSortComparer(a: GridItemElement, b: GridItemElement): number {
    let order = a.props.row - b.props.row;
    if (order === 0) {
        order = a.props.column - b.props.column;
    }
    return order;
}
